import React from "react";
import { Link } from "react-router-dom";
import ContactForm from "../components/ContactForm";
import PixelHeroScene from "../components/PixelHeroScene";
import PixelCircuitScene from "../components/PixelCircuitScene";
import PixelIcon from "../components/PixelIcon";
import BrandIcon from "../components/BrandIcon";
import { Reveal, Float } from "../components/motion/Reveal";
import blogPosts from "../data/blogPosts";

const HomePage = () => {
  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="hero" id="hero" aria-label="Introduction">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <Reveal mount>
                <p className="section-label">Product-Driven Tech Studio</p>
              </Reveal>
              <Reveal mount delay={0.08}>
                <h1>
                  We Build Digital Products That{" "}
                  <span className="gradient-text">Actually Ship</span>
                </h1>
              </Reveal>
              <Reveal mount delay={0.16}>
                <p className="hero-sub">
                  SaaS platforms, AI systems, and blockchain applications —
                  designed, built, and scaled by a product-first engineering
                  studio.
                </p>
              </Reveal>
              <Reveal mount delay={0.24}>
                <div className="hero-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => scrollToSection("contact")}
                  >
                    Book a Technical Consultation
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => scrollToSection("products")}
                  >
                    Explore Our Products
                  </button>
                </div>
              </Reveal>
            </div>
            <Reveal mount delay={0.2} className="hero-visual">
              <Float className="hero-image-wrap">
                <PixelHeroScene />
              </Float>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section className="about" id="about" aria-label="About Hayden Bulk Tech Labs">
        <div className="container">
          <div className="about-grid">
            <Reveal className="about-text">
              <p className="section-label">About Us</p>
              <h2>A Studio That Ships Production Systems</h2>
              <p>
                Hayden Bulk Tech Labs is a product-driven technology studio
                focused on building real, revenue-ready digital products.
              </p>
              <p>
                We don't just consult — we design, ship, iterate, and operate
                production systems across AI, SaaS, blockchain, and mobile
                platforms.
              </p>
              <p>
                Our work spans content platforms, Web3 games and
                on-chain systems, virtual try-on and computer vision products,
                and consumer and B2B SaaS applications.
              </p>
            </Reveal>
            <div>
              <div className="about-highlights">
                <Reveal className="about-highlight" delay={0}>
                  <div className="service-icon" aria-hidden="true">
                    <PixelIcon name="spark" size={18} />
                  </div>
                  <h4>AI-Powered Solutions</h4>
                  <p>
                    Language models, computer vision, and intelligent automation
                    integrated into real products.
                  </p>
                </Reveal>
                <Reveal className="about-highlight" delay={0.07}>
                  <div className="service-icon" aria-hidden="true">
                    <PixelIcon name="chain" size={18} />
                  </div>
                  <h4>Web3 &amp; Blockchain</h4>
                  <p>
                    On-chain systems, smart contracts, and token-based economies
                    built for production.
                  </p>
                </Reveal>
                <Reveal className="about-highlight" delay={0.14}>
                  <div className="service-icon" aria-hidden="true">
                    <PixelIcon name="stack" size={18} />
                  </div>
                  <h4>Scalable Architecture</h4>
                  <p>
                    Cloud-native infrastructure designed to handle growth from
                    day one.
                  </p>
                </Reveal>
                <Reveal className="about-highlight" delay={0.21}>
                  <div className="service-icon" aria-hidden="true">
                    <PixelIcon name="rocket" size={18} />
                  </div>
                  <h4>Full-Stack Execution</h4>
                  <p>
                    From product design through deployment and operations — end
                    to end.
                  </p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRODUCTS ===== */}
      <section className="products" id="products" aria-label="Our products">
        <div className="container">
          <Reveal className="section-header">
            <p className="section-label">Products</p>
            <h2>What We've Built</h2>
            <p className="section-intro">
              Production systems serving real users — not prototypes or pitch
              decks.
            </p>
          </Reveal>

          <div className="product-grid">
            {/* Privateer */}
            <Reveal className="product-card">
              <div className="product-logo" style={{ background: "#fff" }}>
                <img
                  src="/privateer-logo.png"
                  alt="Privateer logo"
                  width="72"
                  height="72"
                  loading="lazy"
                  style={{ objectFit: "contain", padding: "4px" }}
                />
              </div>
              <div className="product-content">
                <h3>Privateer</h3>
                <p className="product-tagline">
                  Chart your own course, privately.
                </p>
                <p className="product-desc">
                  A privacy-first AI platform with encrypted conversations and
                  zero-data-retention inference. Talk to frontier models,
                  generate images and video, sketch on the whiteboard, build
                  shareable Cargo, and code from the CLI agent — without your
                  data ever becoming the product. Available now on Android,
                  iOS, and the Solana Seeker dApp Store.
                </p>
                <ul className="product-features">
                  <li>Encrypted conversations, zero data retention</li>
                  <li>Multi-model access to frontier AI</li>
                  <li>Image &amp; video generation</li>
                  <li>Whiteboard + Cargo builder</li>
                  <li>CLI coding agent</li>
                  <li>Developer API</li>
                </ul>
                {/* TODO: swap App Store / Solana dApp Store URLs for direct store links */}
                <div className="store-badges">
                  <a
                    className="store-badge"
                    href="https://play.google.com/store/apps/details?id=app.privateer"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get Privateer on Google Play"
                  >
                    <span className="store-badge-icon">
                      <BrandIcon name="googleplay" size={18} />
                    </span>
                    <span>
                      <small>Get it on</small>
                      <strong>Google Play</strong>
                    </span>
                  </a>
                  <a
                    className="store-badge"
                    href="https://privateer.pro"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download Privateer on the App Store"
                  >
                    <span className="store-badge-icon">
                      <BrandIcon name="apple" size={18} />
                    </span>
                    <span>
                      <small>Download on the</small>
                      <strong>App Store</strong>
                    </span>
                  </a>
                  <a
                    className="store-badge"
                    href="https://privateer.pro"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get Privateer on the Solana dApp Store"
                  >
                    <span className="store-badge-icon">
                      <BrandIcon name="solana" size={16} />
                    </span>
                    <span>
                      <small>Available on the</small>
                      <strong>Solana dApp Store</strong>
                    </span>
                  </a>
                </div>
                <div className="product-meta">
                  <a
                    href="https://privateer.pro"
                    className="btn btn-ghost"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Privateer
                  </a>
                  <span className="product-platform">
                    Android &middot; iOS &middot; Solana Seeker &middot; Web
                  </span>
                </div>
              </div>
            </Reveal>

            {/* ChattyBooks */}
            <Reveal className="product-card">
              <div className="product-logo" style={{ background: "#000" }}>
                <img
                  src="/chattybook-logo.png"
                  alt="ChattyBooks logo"
                  width="72"
                  height="72"
                  loading="lazy"
                  style={{ objectFit: "contain", padding: "4px" }}
                />
              </div>
              <div className="product-content">
                <h3>ChattyBooks</h3>
                <p className="product-tagline">
                  AI-Powered Interactive Reading Platform
                </p>
                <p className="product-desc">
                  An AI-driven platform that transforms the reading experience
                  into an interactive, personalized journey. ChattyBooks uses
                  language models to generate adaptive content, enabling users to
                  engage with stories in ways that static text never could.
                </p>
                <ul className="product-features">
                  <li>AI-generated interactive narratives</li>
                  <li>Personalized reading experiences</li>
                  <li>Real-time content adaptation</li>
                  <li>Multi-format story support</li>
                  <li>Intelligent content recommendations</li>
                  <li>Cross-platform accessibility</li>
                </ul>
                <div className="product-meta">
                  <a
                    href="https://www.chattybooks.com"
                    className="btn btn-ghost"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit ChattyBooks
                  </a>
                  <span className="product-platform">Web Platform</span>
                </div>
              </div>
            </Reveal>

            {/* Full Groove Radio */}
            <Reveal className="product-card">
              <div className="product-logo" style={{ background: "#000" }}>
                <img
                  src="/fullgrooveradio-logo.png"
                  alt="Full Groove Radio logo"
                  width="72"
                  height="72"
                  loading="lazy"
                  style={{ objectFit: "contain", padding: "4px" }}
                />
              </div>
              <div className="product-content">
                <h3>Full Groove Radio</h3>
                <p className="product-tagline">
                  Fully Automated AI Media Generation Pipeline
                </p>
                <p className="product-desc">
                  An experimental, fully automated daily media generation
                  pipeline building a fictional world through AI-generated
                  audio, video, and imagery distributed across YouTube, TikTok,
                  Instagram, and the web — every day, without human
                  intervention.
                </p>
                <ul className="product-features">
                  <li>Daily AI-generated audio and video content</li>
                  <li>Multi-platform automated distribution</li>
                  <li>Persistent fictional world-building</li>
                  <li>End-to-end generative media pipeline</li>
                  <li>Cross-format narrative continuity</li>
                  <li>Zero-touch publishing workflow</li>
                </ul>
                <div className="social-links" style={{ marginBottom: "20px" }}>
                  <a
                    className="social-link"
                    href="https://www.youtube.com/@FullGrooveRadio"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BrandIcon name="youtube" size={14} />
                    YouTube
                  </a>
                  <a
                    className="social-link"
                    href="https://www.instagram.com/fullgrooveradio/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BrandIcon name="instagram" size={14} />
                    Instagram
                  </a>
                  <a
                    className="social-link"
                    href="https://www.tiktok.com/@fullgrooveradio"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BrandIcon name="tiktok" size={14} />
                    TikTok
                  </a>
                </div>
                <div className="product-meta">
                  <a
                    href="https://www.fullgrooveradio.com"
                    className="btn btn-ghost"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Full Groove Radio
                  </a>
                  <span className="product-platform">Web / YouTube / TikTok / Instagram</span>
                </div>
              </div>
            </Reveal>

            {/* Farty Unicorn */}
            <Reveal className="product-card">
              <div className="product-logo" style={{ background: "#fff" }}>
                <img
                  src="/farty-unicorn.png"
                  alt="Farty Unicorn mascot"
                  width="72"
                  height="72"
                  loading="lazy"
                  style={{ objectFit: "contain", padding: "6px" }}
                />
              </div>
              <div className="product-content">
                <h3>Farty Unicorn</h3>
                <p className="product-tagline">
                  Blockchain-Powered Mobile Game
                </p>
                <p className="product-desc">
                  A Web3 mobile game that combines engaging gameplay with
                  blockchain-based mechanics. Farty Unicorn features on-chain
                  assets, token-based rewards, and decentralized game economies
                  — proving that blockchain gaming can be fun and accessible.
                  Available now on the Solana Seeker dApp Store.
                </p>
                <ul className="product-features">
                  <li>On-chain game assets and NFTs</li>
                  <li>Token-based reward system</li>
                  <li>Decentralized game economy</li>
                  <li>Cross-platform mobile gameplay</li>
                  <li>Smart contract integration</li>
                  <li>Community-driven development</li>
                </ul>
                {/* TODO: no public per-app dApp Store URL — make this a link if one becomes available */}
                <div className="store-badges">
                  <span className="store-badge store-badge-static">
                    <span className="store-badge-icon">
                      <BrandIcon name="solana" size={16} />
                    </span>
                    <span>
                      <small>Available on the</small>
                      <strong>Solana dApp Store</strong>
                    </span>
                  </span>
                </div>
                <div className="product-meta">
                  <span className="product-platform">Mobile &middot; Web3 &middot; Solana Seeker</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="services" id="services" aria-label="Our services">
        <div className="container">
          <Reveal className="section-header">
            <p className="section-label">Services</p>
            <h2>What We Do</h2>
            <p className="section-intro">
              End-to-end technical capabilities for companies building digital
              products.
            </p>
          </Reveal>

          <div className="services-grid">
            <Reveal className="service-item" delay={0}>
              <div className="service-icon" aria-hidden="true">
                <PixelIcon name="window" size={18} />
              </div>
              <h3>SaaS Product Development</h3>
              <p>
                Full-stack design, development, and deployment of
                subscription-based software platforms from zero to production.
              </p>
            </Reveal>
            <Reveal className="service-item" delay={0.06}>
              <div className="service-icon" aria-hidden="true">
                <PixelIcon name="spark" size={18} />
              </div>
              <h3>AI &amp; Machine Learning Solutions</h3>
              <p>
                Custom AI integrations, language model applications, computer
                vision systems, and intelligent automation pipelines.
              </p>
            </Reveal>
            <Reveal className="service-item" delay={0.12}>
              <div className="service-icon" aria-hidden="true">
                <PixelIcon name="chain" size={18} />
              </div>
              <h3>Blockchain &amp; Web3 Development</h3>
              <p>
                Smart contracts, token economies, on-chain applications, and
                decentralized system architecture.
              </p>
            </Reveal>
            <Reveal className="service-item" delay={0.18}>
              <div className="service-icon" aria-hidden="true">
                <PixelIcon name="target" size={18} />
              </div>
              <h3>Technical Consulting</h3>
              <p>
                Architecture reviews, technology selection, scaling strategies,
                and hands-on technical advisory for product teams.
              </p>
            </Reveal>
            <Reveal className="service-item" delay={0.24}>
              <div className="service-icon" aria-hidden="true">
                <PixelIcon name="arrow" size={18} />
              </div>
              <h3>Product &amp; Technical Marketing</h3>
              <p>
                Go-to-market strategy, technical content, developer
                documentation, and product positioning for technical audiences.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== WHY US ===== */}
      <section className="why-us" id="why-us" aria-label="Why work with us">
        <div className="container">
          <div className="why-grid">
            <Reveal>
              <p className="section-label">Why Us</p>
              <h2>Built Different</h2>
              <p className="section-intro" style={{ marginBottom: "32px" }}>
                We operate like a product company, not an agency. Every
                engagement is treated like our own product launch.
              </p>
              <ul className="why-list">
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>Product-first mindset</strong>
                    <br />
                    <span>
                      We think in outcomes and user value, not billable hours or
                      feature checklists.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>AI &amp; blockchain fluency</strong>
                    <br />
                    <span>
                      Deep, production-level experience with the technologies
                      that matter.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>End-to-end execution</strong>
                    <br />
                    <span>
                      From architecture and design through deployment,
                      monitoring, and iteration.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>Modern scalable stacks</strong>
                    <br />
                    <span>
                      React, Node, AWS, Solana, and AI/ML tooling — chosen for
                      the problem, not the trend.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>Founder-level involvement</strong>
                    <br />
                    <span>
                      Senior engineers on every project. No handoffs to junior
                      teams.
                    </span>
                  </div>
                </li>
              </ul>
            </Reveal>
            <Reveal className="why-image" delay={0.1}>
              <PixelCircuitScene />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== ENGAGEMENT MODELS ===== */}
      <section
        className="engagement"
        id="engagement"
        aria-label="Engagement models"
      >
        <div className="container">
          <Reveal className="section-header">
            <p className="section-label">Engagement Models</p>
            <h2>How We Work</h2>
            <p className="section-intro">
              Flexible structures designed for different stages and scopes.
            </p>
          </Reveal>

          <div className="engagement-grid">
            <Reveal className="engagement-card" delay={0}>
              <div className="engagement-num">01</div>
              <h3>Fixed-Scope MVP Builds</h3>
              <p>
                Defined scope, timeline, and deliverables. Ideal for validating
                a product concept and getting to market.
              </p>
            </Reveal>
            <Reveal className="engagement-card" delay={0.06}>
              <div className="engagement-num">02</div>
              <h3>Monthly Retainers</h3>
              <p>
                Ongoing development capacity for teams that need consistent
                engineering support and iteration cycles.
              </p>
            </Reveal>
            <Reveal className="engagement-card" delay={0.12}>
              <div className="engagement-num">03</div>
              <h3>Technical Advisory</h3>
              <p>
                Strategic guidance on architecture, AI integration, blockchain
                strategy, and technology decisions.
              </p>
            </Reveal>
            <Reveal className="engagement-card" delay={0.18}>
              <div className="engagement-num">04</div>
              <h3>Co-Development Partnerships</h3>
              <p>
                Shared ownership and equity-based partnerships for founders
                building ambitious technical products.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== BLOG PREVIEW ===== */}
      <section className="blog-section" id="blog" aria-label="Blog">
        <div className="container">
          <Reveal className="section-header">
            <p className="section-label">Blog</p>
            <h2>Insights &amp; Technical Writing</h2>
            <p className="section-intro">
              Practical perspectives on building AI-powered products, SaaS
              development, and emerging technology.
            </p>
          </Reveal>

          <div className="blog-preview">
            {blogPosts.slice(0, 2).map((post, i) => (
              <Reveal className="blog-card" key={post.slug} delay={i * 0.08}>
                <span className="blog-tag">{post.tag}</span>
                <h3>
                  {post.published ? (
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  ) : (
                    <span style={{ color: "var(--text)" }}>{post.title}</span>
                  )}
                </h3>
                <p>{post.description}</p>
                <span className="blog-date">
                  {post.date}
                  {post.readTime ? ` · ${post.readTime}` : ""}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT FORM ===== */}
      <ContactForm />
    </>
  );
};

export default HomePage;
